import React, { useEffect, useMemo, useState, useCallback } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import PrisonerBadge from "@/components/PrisonerBadge";
import {
    Loader2, Hash, Mic, Award, Medal, Ban, ChevronDown, ChevronRight, Plus, X, Activity,
    Key, Copy, RefreshCcw, ShieldOff,
} from "lucide-react";

/**
 * Team · iter 31.25 (merged Roster + Built By).
 *
 * Per No. 6: "I can't see any changes in the roster or the built by
 * page and wonder also if they're the same thing" → option 2: merge.
 *
 * One card per person. Top half = roster info (number, status, trust,
 * activity, role). Expandable bottom half = award citation (the speech,
 * the thanks, the acceptance). Extras (Ern, Internet, Banana — citation
 * but no roster number) render as a separate "Honourable extras" grid.
 *
 * The Operator's row shows the signed-in user's display name, not a
 * hardcoded "Matt".
 */

// -------------------- small pill atoms (copied from old Roster.jsx)
function TrustPill({ value }) {
    const v = (value || "").toString();
    const cls = v.startsWith("[UNKNOWN")
        ? "border-stamp-red text-stamp-red"
        : v === "Yes" || v === "Provisional" || v === "Structural"
            ? "border-stamp-green text-stamp-green"
            : "border-ink/40 text-ink-soft";
    return (
        <span className={`inline-block border px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${cls}`}>
            {v}
        </span>
    );
}
function ActivePill({ value }) {
    const v = (value || "").toString();
    const tone = v.startsWith("Yes")
        ? "border-stamp-green text-stamp-green"
        : v.startsWith("No")
            ? "border-ink/40 text-ink-soft"
            : "border-ink/30 text-ink";
    return (
        <span className={`inline-block border px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${tone}`}>
            {v}
        </span>
    );
}
function StatusPill({ value }) {
    const tone = {
        "Permanent": "bg-ink text-paper-light",
        "Probationary": "border border-stamp-red text-stamp-red",
        "Active": "border border-ink/40 text-ink",
        "Seasonal": "border border-dashed border-ink/40 text-ink-soft",
        "Variable": "border border-dashed border-stamp-red text-stamp-red",
    }[value] || "border border-ink/30 text-ink";
    return (
        <span className={`inline-block px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em] ${tone}`}>
            {value}
        </span>
    );
}

// -------------------- main team member card
// -------------------- comms inbox panel (iter 31.33)
// Read-only per-agent inbox surfacing `comms_outbound` rows addressed
// to the agent. Sources include Auto-Brief Dispatch (per iter 31.32)
// and routing-rule `notify_two` actions. Lazy-loads on expand.
//
// IMPORTANT (Emergent platform quirk): axios/fetch calls inside child-
// component useEffect hooks are wrapped by the platform instrumentation,
// which trips on the cloned Request object and silently drops the
// promise. So `loadComms` is owned by the parent Team component and
// passed down — calls from the parent scope work fine.
function CommsInboxPanel({ agentKey, agentName, comms, onLoad }) {
    const [open, setOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const err = comms?.err || "";
    const data = comms?.data || null;

    useEffect(() => { if (open && data === null && !err) onLoad?.(agentKey); }, [open, data, err, onLoad, agentKey]);

    return (
        <div className="mt-4 pt-3 border-t border-dashed border-ink/20" data-testid={`team-comms-${agentKey}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`team-comms-toggle-${agentKey}`}
                className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
            >
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Comms inbox{data?.total != null ? ` · ${data.total}` : ""}
            </button>

            {open && (
                <div className="mt-3 space-y-2">
                    {err && (
                        <div
                            className="font-body text-xs text-stamp-red border border-stamp-red/40 px-2 py-1"
                            data-testid={`team-comms-error-${agentKey}`}
                        >
                            {err}
                        </div>
                    )}
                    {data === null && !err && (
                        <div className="label-typed text-[10px]">
                            <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                            Loading inbox…
                        </div>
                    )}
                    {data?.messages?.length === 0 && (
                        <div className="font-body text-xs text-ink-soft italic">
                            No comms filed to {agentName} yet. Auto-Brief Dispatch and routing-rule
                            <code className="text-[10px] mx-1">notify_two</code> actions land here.
                        </div>
                    )}
                    {data?.messages?.map((m) => (
                        <CommsRow
                            key={m.id}
                            m={m}
                            expanded={expandedId === m.id}
                            onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
                        />
                    ))}
                    {data?.has_more && (
                        <div className="label-typed text-[10px] italic text-ink-soft">
                            Showing first {data.count} of {data.total}. Pagination wiring
                            pending — open the database for older rows.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CommsRow({ m, expanded, onToggle }) {
    const filedAt = (m.filed_at || "").replace("T", " ").slice(0, 19);
    const sourcePill = {
        auto_brief_dispatch: { label: "Auto-Brief", cls: "border-stamp-red text-stamp-red" },
        routing_rule: { label: "Rule fired", cls: "border-amber-400 text-amber-400" },
    }[m.source] || { label: m.source || "comms", cls: "border-ink/30 text-ink" };
    const previewLine = (m.body_md || "").split("\n").find((l) => l.trim() && !l.startsWith("#")) || "";
    return (
        <div className="paper-card p-3 bg-paper-dark/40" data-testid={`team-comms-row-${m.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-1.5 mb-1">
                        <span className={`inline-block border px-1.5 py-0.5 font-heading uppercase text-[9px] tracking-[0.18em] ${sourcePill.cls}`}>
                            {sourcePill.label}
                        </span>
                        <span className="label-typed text-[9px]">{filedAt}</span>
                    </div>
                    <div className="font-body text-xs text-ink leading-snug line-clamp-2">
                        {previewLine.slice(0, 200) || (m.body_md || "").slice(0, 200)}
                    </div>
                </div>
                <button
                    onClick={onToggle}
                    data-testid={`team-comms-expand-${m.id}`}
                    className="font-heading uppercase text-[9px] tracking-[0.22em] text-ink-soft hover:text-stamp-red shrink-0"
                >
                    {expanded ? "− body" : "+ body"}
                </button>
            </div>
            {expanded && (
                <div className="mt-3 pt-3 border-t border-dashed border-ink/15" data-testid={`team-comms-body-${m.id}`}>
                    <pre className="font-body text-[11px] text-ink whitespace-pre-wrap leading-relaxed">
{m.body_md || "(no body)"}
                    </pre>
                </div>
            )}
        </div>
    );
}


// -------------------- routing rules panel (iter 31.32)
// Collapsible per-card panel. Operator types natural language; Claude
// compiles via POST /api/agents/{key}/routing-rules; rule lands pending;
// Two approves via the inline approve button. Amendments preserve the
// previous_compiled tuple + show a diff_summary so Two reviews what
// changed, not just what it now says.
function RoutingRulesPanel({ agentKey, agentName }) {
    const [open, setOpen] = useState(false);
    const [rules, setRules] = useState(null);
    const [err, setErr] = useState("");
    const [creating, setCreating] = useState(false);
    const [newText, setNewText] = useState("");
    const [busy, setBusy] = useState(false);
    const [amendingId, setAmendingId] = useState(null);
    const [amendText, setAmendText] = useState("");
    const [expandedRuleIds, setExpandedRuleIds] = useState({});

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/agents/${agentKey}/routing-rules`);
            setRules(data.rules || []);
            setErr("");
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        }
    }, [agentKey]);

    useEffect(() => {
        if (open && rules === null) { load(); }
    }, [open, rules, load]);

    const submitNew = async () => {
        if (!newText.trim()) return;
        setBusy(true); setErr("");
        try {
            await api.post(`/agents/${agentKey}/routing-rules`, { raw_text: newText.trim() });
            setNewText("");
            setCreating(false);
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const submitAmend = async (ruleId) => {
        if (!amendText.trim()) return;
        setBusy(true); setErr("");
        try {
            await api.put(`/agents/${agentKey}/routing-rules/${ruleId}/amend`, { raw_text: amendText.trim() });
            setAmendingId(null);
            setAmendText("");
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const approve = async (ruleId) => {
        setBusy(true); setErr("");
        try {
            await api.put(`/agents/${agentKey}/routing-rules/${ruleId}/approve`, { approved_by: "two" });
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const reject = async (ruleId) => {
        const reason = window.prompt("Reason for rejection (optional):") || "";
        setBusy(true); setErr("");
        try {
            await api.put(`/agents/${agentKey}/routing-rules/${ruleId}/reject`, { rejected_by: "two", reason });
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const remove = async (ruleId) => {
        if (!window.confirm("Delete this rule entirely? Use 'Reject' instead if you want the audit trail to keep it.")) return;
        setBusy(true); setErr("");
        try {
            await api.delete(`/agents/${agentKey}/routing-rules/${ruleId}`);
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const ruleCount = rules?.length ?? null;

    return (
        <div className="mt-4 pt-3 border-t border-dashed border-ink/20" data-testid={`team-rules-${agentKey}`}>
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`team-rules-toggle-${agentKey}`}
                className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
            >
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Routing rules{ruleCount !== null ? ` · ${ruleCount}` : ""}
            </button>

            {open && (
                <div className="mt-3 space-y-3">
                    {err && (
                        <div
                            className="font-body text-xs text-stamp-red border border-stamp-red/40 px-2 py-1"
                            data-testid={`team-rules-error-${agentKey}`}
                        >
                            {err}
                        </div>
                    )}
                    {rules === null && (
                        <div className="label-typed text-[10px]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading rules…</div>
                    )}
                    {rules?.length === 0 && (
                        <div className="font-body text-xs text-ink-soft italic">
                            No routing rules on {agentName}. Type one below — Claude compiles it, Two approves.
                        </div>
                    )}
                    {rules?.map((r) => (
                        <RuleRow
                            key={r.id}
                            r={r}
                            agentKey={agentKey}
                            expanded={!!expandedRuleIds[r.id]}
                            onToggleExpand={() => setExpandedRuleIds((s) => ({ ...s, [r.id]: !s[r.id] }))}
                            amending={amendingId === r.id}
                            amendText={amendText}
                            setAmendText={setAmendText}
                            onStartAmend={() => { setAmendingId(r.id); setAmendText(r.raw_text || ""); }}
                            onCancelAmend={() => { setAmendingId(null); setAmendText(""); }}
                            onSubmitAmend={() => submitAmend(r.id)}
                            onApprove={() => approve(r.id)}
                            onReject={() => reject(r.id)}
                            onDelete={() => remove(r.id)}
                            busy={busy}
                        />
                    ))}

                    {!creating ? (
                        <button
                            onClick={() => setCreating(true)}
                            data-testid={`team-rules-new-${agentKey}`}
                            className="inline-flex items-center gap-2 font-heading uppercase text-[10px] tracking-[0.22em] border border-stamp-red/50 text-stamp-red hover:bg-stamp-red hover:text-paper-light px-3 py-1.5"
                        >
                            <Plus className="w-3 h-3" /> Author a rule
                        </button>
                    ) : (
                        <div className="border border-dashed border-stamp-red/40 p-3 bg-paper-dark/30" data-testid={`team-rules-create-${agentKey}`}>
                            <div className="label-typed mb-2">Plain English · Claude will compile</div>
                            <textarea
                                value={newText}
                                onChange={(e) => setNewText(e.target.value)}
                                placeholder={`e.g. "When a statement from ${agentName} lands, route it to Spy Finance and alert if it exceeds £400."`}
                                rows={3}
                                data-testid={`team-rules-input-${agentKey}`}
                                className="w-full bg-paper-light/60 border border-ink/20 px-2 py-1.5 font-body text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-stamp-red"
                            />
                            <div className="mt-2 flex gap-2">
                                <button
                                    onClick={submitNew}
                                    disabled={busy || !newText.trim()}
                                    data-testid={`team-rules-submit-${agentKey}`}
                                    className="inline-flex items-center gap-1 font-heading uppercase text-[10px] tracking-[0.22em] bg-stamp-red text-paper-light px-3 py-1.5 disabled:opacity-40"
                                >
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                    {busy ? "Compiling…" : "Compile + file pending"}
                                </button>
                                <button
                                    onClick={() => { setCreating(false); setNewText(""); }}
                                    disabled={busy}
                                    className="font-heading uppercase text-[10px] tracking-[0.22em] border border-ink/30 text-ink-soft px-3 py-1.5 hover:border-ink hover:text-ink"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Single rule row + (expanded) compiled view + amend form.
function RuleRow({ r, agentKey, expanded, onToggleExpand, amending, amendText, setAmendText, onStartAmend, onCancelAmend, onSubmitAmend, onApprove, onReject, onDelete, busy }) {
    const statusCls = {
        approved: "border-stamp-green text-stamp-green",
        rejected: "border-ink/40 text-ink-soft line-through",
        pending: "border-stamp-red text-stamp-red",
    }[r.status] || "border-ink/30 text-ink";
    const compiled = r.compiled_rule || {};
    return (
        <div className="paper-card p-3 bg-paper-dark/40" data-testid={`team-rule-${agentKey}-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-1.5 mb-1">
                        <span className={`inline-block border px-1.5 py-0.5 font-heading uppercase text-[9px] tracking-[0.18em] ${statusCls}`}>
                            {r.status}
                        </span>
                        {r.diff_summary && (
                            <span className="inline-block border border-amber-400/60 text-amber-400 px-1.5 py-0.5 font-heading uppercase text-[9px] tracking-[0.18em]">
                                Amended · diff ready
                            </span>
                        )}
                    </div>
                    <div className="font-body text-xs text-ink leading-snug">
                        {compiled.natural_summary || r.raw_text}
                    </div>
                    <div className="label-typed text-[9px] italic mt-1">
                        Raw: {r.raw_text}
                    </div>
                </div>
                <button
                    onClick={onToggleExpand}
                    data-testid={`team-rule-expand-${r.id}`}
                    className="font-heading uppercase text-[9px] tracking-[0.22em] text-ink-soft hover:text-stamp-red shrink-0"
                >
                    {expanded ? "− detail" : "+ detail"}
                </button>
            </div>

            {expanded && (
                <div className="mt-3 pt-3 border-t border-dashed border-ink/15 text-[11px] font-body text-ink-soft space-y-2" data-testid={`team-rule-detail-${r.id}`}>
                    <div>
                        <div className="label-typed text-[9px] mb-0.5">Compiled tuple</div>
                        <pre className="bg-paper-light/50 border border-ink/15 p-2 overflow-x-auto whitespace-pre-wrap text-[10px]">
{JSON.stringify(compiled, null, 2)}
                        </pre>
                    </div>
                    {r.previous_compiled && r.diff_lines?.length > 0 && (
                        <div data-testid={`team-rule-diff-${r.id}`}>
                            <div className="label-typed text-[9px] mb-0.5 text-amber-400">Diff vs previous</div>
                            <div className="border border-amber-400/40 bg-amber-400/5 p-2 text-[10px] space-y-1">
                                {r.diff_lines.map((line, i) => (
                                    <div key={i} className="font-mono text-ink whitespace-pre-wrap break-all">{line}</div>
                                ))}
                            </div>
                        </div>
                    )}
                    {r.approval_note && <div>Approval note: <em>{r.approval_note}</em></div>}
                    {r.rejection_reason && <div>Rejection reason: <em>{r.rejection_reason}</em></div>}
                </div>
            )}

            {amending ? (
                <div className="mt-3 border-t border-dashed border-ink/15 pt-3">
                    <div className="label-typed mb-1">Amend · new natural language</div>
                    <textarea
                        value={amendText}
                        onChange={(e) => setAmendText(e.target.value)}
                        rows={3}
                        data-testid={`team-rule-amend-input-${r.id}`}
                        className="w-full bg-paper-light/60 border border-ink/20 px-2 py-1.5 font-body text-xs text-ink focus:outline-none focus:border-stamp-red"
                    />
                    <div className="mt-2 flex gap-2">
                        <button
                            onClick={onSubmitAmend}
                            disabled={busy || !amendText.trim()}
                            data-testid={`team-rule-amend-submit-${r.id}`}
                            className="inline-flex items-center gap-1 font-heading uppercase text-[10px] tracking-[0.22em] bg-amber-400/90 text-ink px-3 py-1 disabled:opacity-40"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                            Recompile + file diff
                        </button>
                        <button
                            onClick={onCancelAmend}
                            disabled={busy}
                            className="font-heading uppercase text-[10px] tracking-[0.22em] border border-ink/30 text-ink-soft px-3 py-1 hover:border-ink hover:text-ink"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {r.status === "pending" && (
                        <>
                            <button
                                onClick={onApprove}
                                disabled={busy}
                                data-testid={`team-rule-approve-${r.id}`}
                                className="font-heading uppercase text-[9px] tracking-[0.22em] border border-stamp-green text-stamp-green hover:bg-stamp-green hover:text-paper-light px-2 py-1"
                            >
                                Approve (Two)
                            </button>
                            <button
                                onClick={onReject}
                                disabled={busy}
                                data-testid={`team-rule-reject-${r.id}`}
                                className="font-heading uppercase text-[9px] tracking-[0.22em] border border-ink/40 text-ink-soft hover:border-stamp-red hover:text-stamp-red px-2 py-1"
                            >
                                Reject
                            </button>
                        </>
                    )}
                    <button
                        onClick={onStartAmend}
                        disabled={busy}
                        data-testid={`team-rule-amend-${r.id}`}
                        className="font-heading uppercase text-[9px] tracking-[0.22em] border border-ink/30 text-ink hover:border-amber-400 hover:text-amber-400 px-2 py-1"
                    >
                        Amend
                    </button>
                    <button
                        onClick={onDelete}
                        disabled={busy}
                        data-testid={`team-rule-delete-${r.id}`}
                        className="font-heading uppercase text-[9px] tracking-[0.22em] border border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red px-2 py-1"
                    >
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
}


// -------------------- agent auth token panel (iter 31.45)
// No. 6 was being asked to paste his JWT into the browser when Two
// needed to read /comms/handler-context. Replaced with per-agent
// scoped tokens — issued once here on /team, handed to the agent
// through a secure channel of No. 6's choosing.
function AgentTokenPanel({ agentKey, agentName }) {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [token, setToken] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get(`/agents/${agentKey}/token-status`);
            setStatus(data);
            setErr("");
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        }
    }, [agentKey]);

    useEffect(() => {
        if (open && status === null) { loadStatus(); }
    }, [open, status, loadStatus]);

    const issue = async () => {
        setBusy(true); setErr(""); setCopied(false);
        try {
            const { data } = await api.post(`/agents/${agentKey}/issue-token`, {});
            setToken(data.token || "");
            await loadStatus();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const revoke = async () => {
        if (!window.confirm(`Revoke ${agentName}'s token? Any agent session using it will be locked out until you re-issue.`)) return;
        setBusy(true); setErr("");
        try {
            await api.delete(`/agents/${agentKey}/token`);
            setToken("");
            await loadStatus();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch { /* clipboard blocked */ }
    };

    const dismissToken = () => setToken("");

    return (
        <div className="mt-4 pt-3 border-t border-dashed border-ink/20">
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`team-token-toggle-${agentKey}`}
                className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] text-stamp-red hover:text-ink"
            >
                <Key className="w-3 h-3" />
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Agent token
                {status?.has_live_token && (
                    <span className="ml-2 inline-block border border-stamp-green text-stamp-green px-2 py-0.5 text-[9px] tracking-[0.18em]">
                        active · v{status.token_version}
                    </span>
                )}
            </button>

            {open && (
                <div className="mt-3" data-testid={`team-token-panel-${agentKey}`}>
                    {err && (
                        <div className="mb-3 border border-stamp-red/40 text-stamp-red font-body text-xs p-2">
                            {err}
                        </div>
                    )}

                    {status && (
                        <div className="mb-3 font-body text-xs text-ink-soft space-y-1">
                            {status.has_live_token ? (
                                <>
                                    <div>
                                        <span className="label-typed tw-prefix mr-1">Status</span>
                                        Live · version {status.token_version} · scopes {(status.scopes || []).join(", ")}
                                    </div>
                                    <div>
                                        <span className="label-typed tw-prefix mr-1">Issued</span>
                                        {status.issued_at?.slice(0, 19).replace("T", " ") || "—"}
                                        {status.issued_by && ` by ${status.issued_by}`}
                                    </div>
                                </>
                            ) : (
                                <div className="italic">
                                    No live token on file for {agentName}. Re-issuing will mint a fresh one.
                                </div>
                            )}
                        </div>
                    )}

                    {token && (
                        <div className="mb-3 border border-stamp-red bg-paper-dark/30 p-3" data-testid={`team-token-display-${agentKey}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="label-typed tw-prefix text-stamp-red">
                                    SHOWN ONCE · COPY NOW
                                </span>
                                <button
                                    onClick={dismissToken}
                                    className="text-ink-soft hover:text-stamp-red"
                                    title="Dismiss (you've copied it)"
                                    data-testid={`team-token-dismiss-${agentKey}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                            <textarea
                                readOnly
                                value={token}
                                className="w-full text-[10px] font-mono bg-paper-light text-ink p-2 border border-ink/20 break-all resize-none"
                                rows={4}
                                data-testid={`team-token-value-${agentKey}`}
                                onFocus={(e) => e.target.select()}
                            />
                            <button
                                onClick={copy}
                                disabled={!token}
                                data-testid={`team-token-copy-${agentKey}`}
                                className="mt-2 inline-flex items-center gap-2 font-heading uppercase text-[10px] tracking-[0.22em] border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper px-3 py-1 disabled:opacity-50"
                            >
                                <Copy className="w-3 h-3" />
                                {copied ? "Copied" : "Copy"}
                            </button>
                            <p className="mt-2 font-body text-[10px] text-ink-faint leading-snug">
                                Hand this to {agentName} through a secure channel. It will not be shown again. Re-issuing rotates and invalidates this copy.
                            </p>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={issue}
                            disabled={busy}
                            data-testid={`team-token-issue-${agentKey}`}
                            className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper px-3 py-1.5 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                            {status?.has_live_token ? "Rotate" : "Issue token"}
                        </button>
                        {status?.has_live_token && (
                            <button
                                onClick={revoke}
                                disabled={busy}
                                data-testid={`team-token-revoke-${agentKey}`}
                                className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] border border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red px-3 py-1.5 disabled:opacity-50"
                            >
                                <ShieldOff className="w-3 h-3" />
                                Revoke
                            </button>
                        )}
                    </div>

                    <p className="mt-3 font-body text-[10px] text-ink-faint leading-snug">
                        The token is a long-lived bearer scoped to <span className="font-mono">/api/comms/*</span> + <span className="font-mono">/api/comms/handler-context</span>. {agentName} authenticates as themselves — No. 6 no longer needs to paste keys.
                    </p>
                </div>
            )}
        </div>
    );
}


function MemberCard({ m, expanded, onToggle, onEdit, comms, onLoadComms }) {
    const cite = m.citation;
    const isEoy = cite?.is_employee_of_the_year;
    return (
        <div
            className={`paper-card p-5 relative ${isEoy ? "border-2 border-stamp-red tape-corner" : ""}`}
            data-testid={`team-member-${m.key}`}
        >
            {isEoy && (
                <div className="absolute -top-3 -right-3 classified-stamp text-[0.7rem]">
                    Employee of the Year · {cite.year}
                </div>
            )}

            {/* Header — number + name + status pills */}
            <div className="flex items-start gap-4">
                {/* Iter 31.76b — Prisoner penny-farthing badge replaces the plain numbered circle. */}
                <div className="flex-shrink-0 border-2 border-stamp-red sf-glow overflow-hidden">
                    <PrisonerBadge agent={m} size={48} title={`No. ${m.number} · ${m.name}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading uppercase text-lg text-ink tracking-[0.04em]">
                        {m.name}
                    </div>
                    <div className="label-typed mt-0.5">{m.alias}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        <StatusPill value={m.status} />
                        <TrustPill value={m.trusted} />
                        <ActivePill value={m.active} />
                        {m.category && (
                            <span className="inline-block border border-ink/30 text-ink-soft px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em]">
                                {m.category}
                            </span>
                        )}
                        {cite && (
                            <span className="inline-flex items-center gap-1 border border-stamp-red text-stamp-red px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em]">
                                <Award className="w-3 h-3" />
                                Cited
                            </span>
                        )}
                        {m.activity_count > 0 && (
                            <span className="inline-flex items-center gap-1 border border-stamp-green text-stamp-green px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em]" title="Recent ledger activity">
                                <Activity className="w-3 h-3" />
                                {m.activity_count}
                            </span>
                        )}
                        {m.auto_brief_recipient && (
                            <span
                                className="inline-flex items-center gap-1 border border-stamp-red text-stamp-red px-2 py-0.5 font-heading uppercase text-[10px] tracking-[0.18em]"
                                title="Receives a copy of every generated Brief"
                                data-testid={`team-auto-brief-pill-${m.key}`}
                            >
                                Auto-Brief
                            </span>
                        )}
                    </div>
                </div>
                {onEdit && m.key && (
                    <button
                        onClick={onEdit}
                        data-testid={`team-edit-${m.key}`}
                        title="Amend dossier"
                        className="font-heading uppercase text-[9px] tracking-[0.22em] border border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red px-2 py-1 shrink-0 self-start"
                    >
                        Edit
                    </button>
                )}
            </div>

            {/* Role + last used + note */}
            <div className="mt-4 font-body text-sm text-ink leading-relaxed">
                {m.role}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-[10px] font-body text-ink-soft uppercase tracking-[0.18em]">
                <span><Hash className="w-3 h-3 inline -mt-0.5 mr-1" />Last used · {m.last_used}</span>
                {m.note && <span>Note · {m.note}</span>}
                {m.quirk && <span>Quirk · {m.quirk}</span>}
            </div>

            {/* Citation toggle */}
            {cite && (
                <div className="mt-4 pt-3 border-t border-dashed border-ink/20">
                    <button
                        onClick={onToggle}
                        data-testid={`team-citation-toggle-${m.key}`}
                        className="inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] text-stamp-red hover:text-ink"
                    >
                        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {expanded ? "Hide citation" : "Read citation"}
                    </button>
                    {expanded && (
                        <div className="mt-3" data-testid={`team-citation-${m.key}`}>
                            <div className="label-typed mb-2">{cite.award || "Permanent citation"}</div>
                            <p className="font-body text-sm text-ink leading-relaxed">{cite.citation}</p>
                            {cite.thanks_from_no6 && (
                                <div className="mt-4 pt-3 border-t border-dashed border-ink/15">
                                    <div className="label-typed tw-prefix mb-2">{cite.thanks_label || "From No. 6"}</div>
                                    <p className="font-body text-sm text-ink italic leading-relaxed">{cite.thanks_from_no6}</p>
                                </div>
                            )}
                            {cite.speech && (
                                <div className="mt-4 pt-3 border-t border-dashed border-ink/15">
                                    <div className="label-typed tw-prefix mb-2">Acceptance</div>
                                    <p className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed">{cite.speech}</p>
                                    <div className="pencil text-base text-right mt-2">— {(cite.alias || m.name).split("—")[0].trim()}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Routing rules panel — iter 31.32, only on agents that have a key
                (extras don't). Operator authors natural language; Claude compiles;
                Two approves; routing layer fires the compiled rule at zero cost. */}
            {m.key && (
                <RoutingRulesPanel agentKey={m.key} agentName={m.name} />
            )}

            {/* Agent auth token panel — iter 31.45. Mint a long-lived scoped
                token so the agent can authenticate to /comms/* directly,
                no key-pasting through No. 6. */}
            {m.key && (
                <AgentTokenPanel agentKey={m.key} agentName={m.name} />
            )}

            {/* Comms inbox panel — iter 31.33. Surfaces any comms_outbound
                rows addressed to this agent (Auto-Brief Dispatch + routing-
                rule notify_two). Read-only — write paths are elsewhere. */}
            {m.key && (
                <CommsInboxPanel
                    agentKey={m.key}
                    agentName={m.name}
                    comms={comms}
                    onLoad={onLoadComms}
                />
            )}
        </div>
    );
}

// -------------------- extras (citation-only personnel)
function ExtraCard({ c, expanded, onToggle }) {
    return (
        <div className="paper-card p-5" data-testid={`team-extra-${c.recipient}`}>
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 border-2 border-stamp-red text-stamp-red rounded-full flex items-center justify-center">
                    <Award className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="label-typed">{c.award || "Permanent citation"}</div>
                    <div className="font-heading uppercase text-sm text-ink tracking-[0.04em] mt-1">
                        {c.alias}
                    </div>
                </div>
            </div>
            <button
                onClick={onToggle}
                data-testid={`team-extra-toggle-${c.recipient}`}
                className="mt-3 inline-flex items-center gap-2 font-heading uppercase text-[11px] tracking-[0.22em] text-stamp-red hover:text-ink"
            >
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {expanded ? "Hide citation" : "Read citation"}
            </button>
            {expanded && (
                <div className="mt-3">
                    <p className="font-body text-sm text-ink leading-relaxed">{c.citation}</p>
                    {c.thanks_from_no6 && (
                        <div className="mt-4 pt-3 border-t border-dashed border-ink/15">
                            <div className="label-typed tw-prefix mb-2">{c.thanks_label || "From No. 6"}</div>
                            <p className="font-body text-sm text-ink italic leading-relaxed">{c.thanks_from_no6}</p>
                        </div>
                    )}
                    {c.speech && (
                        <div className="mt-4 pt-3 border-t border-dashed border-ink/15">
                            <div className="label-typed tw-prefix mb-2">Acceptance</div>
                            <p className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed">{c.speech}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// -------------------- page
export default function Team() {
    const [data, setData] = useState(null);
    const [agents, setAgents] = useState(null);
    const [err, setErr] = useState("");
    const [expanded, setExpanded] = useState({});
    const [filter, setFilter] = useState("all"); // all · cited · roster-only · ai · supplier · contact · ...
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showAutoBriefDialog, setShowAutoBriefDialog] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [suggestedNumbers, setSuggestedNumbers] = useState([]);
    const [commsCache, setCommsCache] = useState({}); // {agentKey: {data | err}}

    // Iter 31.33 — same Emergent platform trap forces the comms-inbox
    // fetcher to live at the parent scope instead of inside the child
    // CommsInboxPanel's useEffect.
    const loadComms = useCallback(async (agentKey) => {
        if (!agentKey) return;
        try {
            const { data: payload } = await api.get(`/agents/${agentKey}/comms`, {
                params: { limit: 25, offset: 0 },
            });
            setCommsCache((c) => ({ ...c, [agentKey]: { data: payload } }));
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "failed";
            setCommsCache((c) => ({ ...c, [agentKey]: { err: `[HTTP ${e?.response?.status || "?"}] ${detail}` } }));
        }
    }, []);

    const loadAll = useCallback(async () => {
        try {
            // Iter 31.29: Team page now joins citations (/team) with the
            // live agents collection (/agents). Live agents win for the
            // operational fields (status, contact, last_contact_at);
            // citations come from the prompts-side built_by payload.
            // Iter 31.32: also fetches the suggested-numbers picker
            // up here — when the same call lives inside the dialog's
            // useEffect the Emergent platform instrumentation traps the
            // fetch and the promise never resolves. Parent-scope works.
            const [teamR, agentsR, suggR] = await Promise.all([
                api.get("/team"),
                api.get("/agents"),
                api.get("/agents/suggested-numbers", { params: { count: 5 } })
                    .catch(() => ({ data: { suggestions: [] } })),
            ]);
            setData(teamR.data);
            setAgents(agentsR.data);
            setSuggestedNumbers(suggR.data.suggestions || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Failed");
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

    // Merge live agents (DB) with citation data (/team). DB is the source
    // of truth for status/category/activity; /team contributes the citation.
    const mergedMembers = useMemo(() => {
        if (!agents) return data?.members || [];
        const citationByKey = {};
        (data?.members || []).forEach((m) => {
            if (m.citation) citationByKey[m.key] = m.citation;
        });
        return (agents.agents || []).map((a) => ({
            key: a.key,
            number: a.number_display || a.number || "—",
            name: a.name,
            alias: a.alias,
            role: a.role,
            status: a.status,
            trusted: a.trusted,
            active: a.active,
            last_used: a.last_used,
            category: a.category,
            citation: citationByKey[a.key] || null,
            activity_count: a.activity_count || 0,
            last_contact_at: a.operational?.last_contact_at,
            contact: a.contact,
            financial: a.financial,
            notes: a.notes,
        }));
    }, [agents, data]);

    const members = mergedMembers;
    const extras = data?.extras || [];
    const categories = agents?.categories || [];

    const filtered = useMemo(() => {
        if (filter === "cited") return members.filter((m) => m.citation);
        if (filter === "roster-only") return members.filter((m) => !m.citation);
        if (filter === "all") return members;
        return members.filter((m) => m.category === filter);
    }, [members, filter]);

    if (err) return <Shell><div className="text-stamp-red label-typed">// {err}</div></Shell>;
    if (!data) return <Shell><div className="flex items-center gap-2 text-ink-soft label-typed"><Loader2 className="w-4 h-4 animate-spin" /> Assembling the team...</div></Shell>;

    const citedCount = members.filter((m) => m.citation).length;

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Personnel · roster ⨝ ceremony</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Medal className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        Team<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Roster and Built By, merged. One card per person. Number, role,
                        status above. Award citation below — tap "Read citation" to expand.
                        {citedCount} of {members.length} on the roster have a citation on file.
                    </p>
                </div>
                <div className="pencil text-lg transform rotate-2 max-w-xs text-right">
                    "Each handler rotates. The file stays." — No. 2
                </div>
            </div>

            {/* Medal panel — small, since the team list is the headline now */}
            <div className="paper-card p-4 mb-6 flex items-center gap-4 tilt-l" data-testid="team-medal">
                <img
                    src={data.medal_image}
                    alt="Knitted medal — the Shed"
                    className="w-20 h-20 object-contain flex-shrink-0"
                    data-testid="team-medal-image"
                />
                <div className="flex-1">
                    <div className="label-typed tw-prefix">The medal</div>
                    <div className="font-body text-xs text-ink mt-1 leading-relaxed">
                        Knitted by hand. Presented on behalf of the operation to every
                        member below. Thank you — all of you.
                    </div>
                </div>
            </div>

            {/* Filter chips + add button */}
            <div className="mb-4 flex items-center gap-2 flex-wrap justify-between" data-testid="team-filters">
                <div className="flex items-center gap-2 flex-wrap">
                    {[
                        { id: "all", label: `All · ${members.length}`, hint: "Every agent on the wall." },
                        { id: "cited", label: `Cited · ${citedCount}`, hint: "Has a permanent award citation on record (Built By page)." },
                        { id: "roster-only", label: `Uncited · ${members.length - citedCount}`, hint: "On the operational roster, no Built By citation yet." },
                        ...categories.map((c) => ({ id: c, label: `${c} · ${members.filter((m) => m.category === c).length}`, hint: `Category: ${c}` })),
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            title={f.hint}
                            data-testid={`team-filter-${f.id}`}
                            className={`px-3 py-1.5 font-heading uppercase text-[11px] tracking-[0.18em] border-2 transition-colors ${
                                filter === f.id
                                    ? "border-stamp-red bg-stamp-red text-paper"
                                    : "border-ink/20 text-ink hover:border-stamp-red hover:text-stamp-red"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowAutoBriefDialog(true)}
                        data-testid="team-auto-brief-bulk-btn"
                        title="Bulk-toggle Auto-Brief Dispatch recipients"
                        className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-ink/30 text-ink-soft font-heading uppercase text-[11px] tracking-[0.18em] hover:border-stamp-red hover:text-stamp-red"
                    >
                        Auto-Brief recipients
                    </button>
                    <button
                        onClick={() => setShowAddDialog(true)}
                        data-testid="team-add-agent-btn"
                        className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-stamp-red text-stamp-red font-heading uppercase text-[11px] tracking-[0.18em] hover:bg-stamp-red hover:text-paper"
                    >
                        <Plus className="w-3 h-3" /> File an agent
                    </button>
                </div>
            </div>

            {showAddDialog && (
                <AddAgentDialog
                    categories={categories}
                    suggestions={suggestedNumbers}
                    onClose={() => setShowAddDialog(false)}
                    onCreated={async () => { setShowAddDialog(false); await loadAll(); }}
                />
            )}

            {showAutoBriefDialog && (
                <AutoBriefBulkDialog
                    agents={agents?.agents || []}
                    onClose={() => setShowAutoBriefDialog(false)}
                    onSaved={async () => { setShowAutoBriefDialog(false); await loadAll(); }}
                />
            )}

            {/* Members grid */}
            <div className="grid lg:grid-cols-2 gap-5 mb-10" data-testid="team-members">
                {filtered.map((m) => (
                    <MemberCard
                        key={m.key}
                        m={m}
                        expanded={!!expanded[m.key]}
                        onToggle={() => toggle(m.key)}
                        comms={commsCache[m.key]}
                        onLoadComms={loadComms}
                        onEdit={() => {
                            // Pull the full agent dossier (db.agents) so the edit form
                            // gets fields like auto_brief_recipient that aren't on the
                            // merged team view payload.
                            const fromAgents = (agents?.agents || []).find((a) => a.key === m.key);
                            setEditingAgent(fromAgents || m);
                        }}
                    />
                ))}
            </div>

            {editingAgent && (
                <EditAgentDialog
                    agent={editingAgent}
                    categories={categories}
                    onClose={() => setEditingAgent(null)}
                    onSaved={async () => { setEditingAgent(null); await loadAll(); }}
                />
            )}

            {/* Citation-only extras (Ern, Internet, Banana, etc.) */}
            {extras.length > 0 && (
                <div className="mb-10" data-testid="team-extras">
                    <div className="label-typed tw-prefix mb-4">
                        Honourable extras · citation, no number
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {extras.map((c) => (
                            <ExtraCard
                                key={c.recipient}
                                c={c}
                                expanded={!!expanded[`extra-${c.recipient}`]}
                                onToggle={() => toggle(`extra-${c.recipient}`)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Dishonourable mentions */}
            {data.dishonourable_mentions?.length > 0 && (
                <div className="mb-10" data-testid="team-dishonour">
                    <div className="label-typed tw-prefix mb-4">
                        <Ban className="w-3 h-3 inline mr-1" />Dishonourable mentions · awards withheld
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {data.dishonourable_mentions.map((m) => (
                            <div
                                key={m.recipient}
                                className="paper-card p-3 relative opacity-80"
                                data-testid={`team-dishonour-${m.recipient}`}
                            >
                                <div className="absolute top-1 right-2 stamp-red text-[9px]">Withheld</div>
                                <div className="label-typed">{m.recipient}</div>
                                <div className="font-heading text-sm uppercase tracking-[0.04em] text-ink">{m.alias}</div>
                                <p className="font-body text-xs text-ink-soft mt-2 leading-relaxed">{m.note}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Voice cast strip */}
            <div className="paper-card tape-corner p-5 tilt-r" data-testid="team-voice-cast">
                <div className="flex items-center gap-2 label-typed tw-prefix mb-3">
                    <Mic className="w-3 h-3" />
                    Voice cast — rotates between Radio drops
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {(data.voice_cast || []).map((v) => (
                        <div
                            key={v.id}
                            className={`border border-ink/25 px-3 py-2 font-body text-xs flex items-center justify-between ${v.rotation === "special" ? "border-dashed bg-paper-dark/30" : "bg-paper-light/50"}`}
                        >
                            <span className="text-ink">{v.label}</span>
                            <span className="label-typed text-[9px]">{v.rotation}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="paper-card p-4 mt-6 tilt-l" data-testid="team-signoff">
                <div className="label-typed text-center">{data.signoff}</div>
            </div>
        </Shell>
    );
}

// -------------------- bulk auto-brief recipients dialog (iter 31.35)
// Lets the operator flick the Auto-Brief Dispatch flag across multiple
// agents in one pass instead of opening each Edit dialog separately.
// Backend: PUT /api/agents/auto-brief-bulk with {set: [{key, on}, ...]}.
function AutoBriefBulkDialog({ agents, onClose, onSaved }) {
    // Initial selection mirrors current `auto_brief_recipient` so the
    // operator sees the current state and only files diffs.
    const [selected, setSelected] = useState(() => {
        const m = {};
        for (const a of agents) {
            if (a.auto_brief_recipient) m[a.key] = true;
        }
        return m;
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const sorted = useMemo(() => {
        return [...agents].sort((a, b) => {
            // Numbered first (asc), then unnumbered alpha
            const an = typeof a.number === "number" ? a.number : 999;
            const bn = typeof b.number === "number" ? b.number : 999;
            if (an !== bn) return an - bn;
            return (a.name || "").localeCompare(b.name || "");
        });
    }, [agents]);

    const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
    const selectAll = () => {
        const m = {};
        for (const a of sorted) m[a.key] = true;
        setSelected(m);
    };
    const clearAll = () => setSelected({});

    const submit = async () => {
        setBusy(true); setErr("");
        try {
            // Build the diff: only file rows where current ≠ desired.
            const set = sorted
                .filter((a) => Boolean(a.auto_brief_recipient) !== Boolean(selected[a.key]))
                .map((a) => ({ key: a.key, on: !!selected[a.key] }));
            if (set.length === 0) {
                onClose();
                return;
            }
            await api.put("/agents/auto-brief-bulk", { set });
            await onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const onCount = Object.values(selected).filter(Boolean).length;

    return (
        <div
            className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="team-auto-brief-bulk-backdrop"
        >
            <div
                className="paper-card p-6 max-w-2xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="team-auto-brief-bulk-dialog"
            >
                <div className="flex items-center justify-between mb-4 shrink-0">
                    <div>
                        <div className="label-typed tw-prefix">Auto-Brief Dispatch · bulk</div>
                        <div className="font-heading text-2xl uppercase tracking-[0.04em] text-ink">
                            Who gets a copy of the Brief?
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="team-auto-brief-bulk-close">
                        <X className="w-5 h-5 text-ink-soft hover:text-stamp-red" />
                    </button>
                </div>

                <div className="font-body text-xs text-ink-soft mb-3 leading-relaxed shrink-0">
                    Tick the agents who should receive a Markdown copy of every Brief in their
                    <code className="text-[10px] mx-1">comms_outbound</code> stream. Only diffs are filed.
                </div>

                <div className="flex items-center gap-2 mb-3 shrink-0">
                    <button
                        onClick={selectAll}
                        data-testid="team-auto-brief-select-all"
                        className="font-heading uppercase text-[10px] tracking-[0.18em] border border-ink/30 text-ink-soft px-2 py-1 hover:border-stamp-red hover:text-stamp-red"
                    >
                        Select all
                    </button>
                    <button
                        onClick={clearAll}
                        data-testid="team-auto-brief-clear"
                        className="font-heading uppercase text-[10px] tracking-[0.18em] border border-ink/30 text-ink-soft px-2 py-1 hover:border-stamp-red hover:text-stamp-red"
                    >
                        Clear
                    </button>
                    <div className="ml-auto label-typed text-[10px]">
                        {onCount} of {sorted.length} on
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto border border-ink/15 bg-paper-dark/30" data-testid="team-auto-brief-bulk-list">
                    {sorted.map((a) => (
                        <label
                            key={a.key}
                            className="flex items-center gap-3 px-3 py-2 border-b border-ink/10 cursor-pointer hover:bg-paper-light/40"
                            data-testid={`team-auto-brief-row-${a.key}`}
                        >
                            <input
                                type="checkbox"
                                checked={!!selected[a.key]}
                                onChange={() => toggle(a.key)}
                                data-testid={`team-auto-brief-check-${a.key}`}
                            />
                            <span className="font-heading text-[11px] tracking-[0.15em] text-ink-soft w-12 shrink-0">
                                {typeof a.number === "number" ? `No. ${a.number}` : "—"}
                            </span>
                            <span className="font-body text-sm text-ink flex-1 truncate">
                                {a.name}
                                {a.alias ? <span className="text-ink-soft"> · {a.alias}</span> : null}
                            </span>
                            <span className="label-typed text-[9px] shrink-0">{a.category || "other"}</span>
                            {a.auto_brief_recipient && !selected[a.key] && (
                                <span className="text-[9px] font-heading uppercase tracking-[0.18em] text-amber-400 shrink-0" title="Currently on — will be switched off">
                                    will turn off
                                </span>
                            )}
                            {!a.auto_brief_recipient && selected[a.key] && (
                                <span className="text-[9px] font-heading uppercase tracking-[0.18em] text-stamp-green shrink-0" title="Currently off — will be switched on">
                                    will turn on
                                </span>
                            )}
                        </label>
                    ))}
                </div>

                {err && (
                    <div className="paper-card border border-stamp-red p-3 mt-3 shrink-0">
                        <pre className="font-body text-xs text-stamp-red whitespace-pre-wrap">{err}</pre>
                    </div>
                )}

                <div className="mt-4 flex items-center justify-end gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border-2 border-ink/25 text-ink-soft font-heading uppercase text-[11px] tracking-[0.18em] hover:border-ink hover:text-ink"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        data-testid="team-auto-brief-bulk-submit"
                        className="px-4 py-2 border-2 border-stamp-red bg-stamp-red text-paper font-heading uppercase text-[11px] tracking-[0.18em] hover:bg-ink disabled:opacity-50 flex items-center gap-2"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        File changes
                    </button>
                </div>
            </div>
        </div>
    );
}


// -------------------- add agent dialog (iter 31.29 / picker 31.32)
// Files a new agent to the registry. Number stays null on creation —
// Two assigns it in the next session via `PUT /agents/{key}/assign-number`.
// Iter 31.32: now fetches the first 5 free canonical numbers and shows
// them as clickable pills so the operator can pre-fill a sensible
// suggestion that Two can confirm or override on next pass.
function AddAgentDialog({ categories, suggestions, onClose, onCreated }) {
    const [key, setKey] = useState("");
    const [name, setName] = useState("");
    const [alias, setAlias] = useState("");
    const [role, setRole] = useState("");
    const [category, setCategory] = useState("other");
    const [autoBrief, setAutoBrief] = useState(false);
    const [suggestedNumber, setSuggestedNumber] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        const cleanKey = key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
        if (!cleanKey || !name.trim()) {
            setErr("Key and name are required.");
            return;
        }
        setBusy(true); setErr("");
        try {
            const { data: created } = await api.post("/agents", {
                key: cleanKey,
                name: name.trim(),
                alias: alias.trim() || null,
                role: role.trim() || null,
                category,
                status: "Active",
                trusted: "[UNKNOWN — seek]",
                active: "Yes",
                last_used: "Recent",
                trust_class: "untrusted",
            });
            // If the operator picked a suggested number, post the assign
            // immediately so the new card lands with a number already set.
            // Two can still amend on next session — the assignment files
            // its own Ledger row tagged `filed_by: three` for the audit.
            if (suggestedNumber && created?.key) {
                try {
                    await api.put(`/agents/${created.key}/assign-number`, {
                        number: suggestedNumber,
                        rationale: "Operator pre-filled from suggested-number picker (iter 31.32) · pending Two's review.",
                    });
                } catch (assignErr) {
                    // Non-fatal — agent was created, just without the number.
                    console.warn("Pre-fill number assignment failed:", assignErr?.message);
                }
            }
            // Toggle Auto-Brief Dispatch flag if operator opted in.
            if (autoBrief && created?.key) {
                try {
                    await api.patch(`/agents/${created.key}`, { auto_brief_recipient: true });
                } catch (briefErr) {
                    console.warn("Auto-brief flag patch failed:", briefErr?.message);
                }
            }
            await onCreated();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "File failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="team-add-dialog-backdrop"
        >
            <div
                className="paper-card p-6 max-w-xl w-full"
                onClick={(e) => e.stopPropagation()}
                data-testid="team-add-dialog"
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="label-typed tw-prefix">File a new agent</div>
                        <div className="font-heading text-2xl uppercase tracking-[0.04em] text-ink">
                            New personnel
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="team-add-dialog-close">
                        <X className="w-5 h-5 text-ink-soft hover:text-stamp-red" />
                    </button>
                </div>

                <div className="font-body text-xs text-ink-soft mb-4 leading-relaxed">
                    Number will be left blank — Two assigns the canonical number in the next session.
                    Every change files to the Ledger as canon (Rule 1).
                </div>

                <div className="space-y-3">
                    <Field label="Key (unique short id, lowercase)" value={key} onChange={setKey} placeholder="e.g. acme_supplier" testid="agent-key" />
                    <Field label="Name" value={name} onChange={setName} placeholder="e.g. Acme Supplier Ltd" testid="agent-name" />
                    <Field label="Alias / role line" value={alias} onChange={setAlias} placeholder="e.g. Tier-1 widget supplier" testid="agent-alias" />
                    <Field label="Notes / dossier" value={role} onChange={setRole} placeholder="Free-form. Markdown ok." multiline testid="agent-role" />
                    <div>
                        <div className="label-typed mb-1">Category</div>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            data-testid="agent-category"
                            className="w-full px-3 py-2 border border-ink/30 bg-paper font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    {/* Suggested-number picker (iter 31.32) — list comes from parent */}
                    {suggestions.length > 0 && (
                        <div data-testid="agent-suggested-numbers">
                            <div className="label-typed mb-1">
                                Suggested number · optional pre-fill, Two confirms
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {suggestions.map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setSuggestedNumber(suggestedNumber === n ? null : n)}
                                        data-testid={`agent-suggested-number-${n}`}
                                        className={`font-heading text-[11px] tracking-[0.18em] px-2.5 py-1 border ${
                                            suggestedNumber === n
                                                ? "border-stamp-red bg-stamp-red text-paper-light"
                                                : "border-ink/30 text-ink hover:border-stamp-red hover:text-stamp-red"
                                        }`}
                                    >
                                        No. {n}
                                    </button>
                                ))}
                                {suggestedNumber !== null && (
                                    <button
                                        type="button"
                                        onClick={() => setSuggestedNumber(null)}
                                        className="font-heading text-[10px] tracking-[0.18em] px-2 py-1 text-ink-soft hover:text-stamp-red"
                                    >
                                        clear
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Auto-Brief Dispatch toggle (iter 31.32) */}
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="agent-auto-brief-row">
                        <input
                            type="checkbox"
                            checked={autoBrief}
                            onChange={(e) => setAutoBrief(e.target.checked)}
                            data-testid="agent-auto-brief"
                            className="mt-1"
                        />
                        <span className="font-body text-xs text-ink">
                            <strong className="font-heading uppercase tracking-[0.15em] text-[10px]">Auto-Brief Dispatch</strong>
                            <span className="block text-ink-soft mt-0.5">
                                Every generated Brief files a copy to this agent's comms_outbound stream automatically.
                            </span>
                        </span>
                    </label>
                </div>

                {err && (
                    <div className="paper-card border border-stamp-red p-3 mt-4">
                        <pre className="font-body text-xs text-stamp-red whitespace-pre-wrap">{err}</pre>
                    </div>
                )}

                <div className="mt-5 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border-2 border-ink/25 text-ink-soft font-heading uppercase text-[11px] tracking-[0.18em] hover:border-ink hover:text-ink"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        data-testid="agent-submit"
                        className="px-4 py-2 border-2 border-stamp-red bg-stamp-red text-paper font-heading uppercase text-[11px] tracking-[0.18em] hover:bg-ink disabled:opacity-50 flex items-center gap-2"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        File agent
                    </button>
                </div>
            </div>
        </div>
    );
}


// -------------------- edit agent dialog (iter 31.32)
// Inline dossier edit — name, alias, role, category, notes, auto-brief
// recipient toggle. PATCH /api/agents/{key}. Number assignment is NOT
// editable here (that's Two's authority).
function EditAgentDialog({ agent, categories, onClose, onSaved }) {
    const [name, setName] = useState(agent.name || "");
    const [alias, setAlias] = useState(agent.alias || "");
    const [role, setRole] = useState(agent.role || "");
    const [notes, setNotes] = useState(agent.notes || "");
    const [category, setCategory] = useState(agent.category || "other");
    const [status, setStatus] = useState(agent.status || "Active");
    const [autoBrief, setAutoBrief] = useState(!!agent.auto_brief_recipient);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async () => {
        if (!name.trim()) { setErr("Name is required."); return; }
        setBusy(true); setErr("");
        try {
            await api.patch(`/agents/${agent.key}`, {
                name: name.trim(),
                alias: alias.trim(),
                role: role.trim(),
                notes: notes.trim(),
                category,
                status,
                auto_brief_recipient: autoBrief,
            });
            await onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="team-edit-dialog-backdrop"
        >
            <div
                className="paper-card p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid={`team-edit-dialog-${agent.key}`}
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="label-typed tw-prefix">Amend dossier · {agent.key}</div>
                        <div className="font-heading text-2xl uppercase tracking-[0.04em] text-ink">
                            {agent.name}{agent.number != null ? ` · No. ${agent.number}` : ""}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="team-edit-dialog-close">
                        <X className="w-5 h-5 text-ink-soft hover:text-stamp-red" />
                    </button>
                </div>

                <div className="font-body text-xs text-ink-soft mb-4 leading-relaxed">
                    Every edit files a `agent_amended` row to the Ledger (canon Rule 1).
                    Number assignment stays with Two — use the Two session to change it.
                </div>

                <div className="space-y-3">
                    <Field label="Name" value={name} onChange={setName} testid="edit-name" />
                    <Field label="Alias" value={alias} onChange={setAlias} testid="edit-alias" />
                    <Field label="Role / function" value={role} onChange={setRole} multiline testid="edit-role" />
                    <Field label="Notes / dossier markdown" value={notes} onChange={setNotes} multiline testid="edit-notes" />
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="label-typed mb-1">Category</div>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                data-testid="edit-category"
                                className="w-full px-3 py-2 border border-ink/30 bg-paper font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                            >
                                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="label-typed mb-1">Status</div>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                data-testid="edit-status"
                                className="w-full px-3 py-2 border border-ink/30 bg-paper font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                            >
                                {["Permanent","Probationary","Active","Seasonal","Variable","Structural","Closed","Dormant"].map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer" data-testid="edit-auto-brief-row">
                        <input
                            type="checkbox"
                            checked={autoBrief}
                            onChange={(e) => setAutoBrief(e.target.checked)}
                            data-testid="edit-auto-brief"
                            className="mt-1"
                        />
                        <span className="font-body text-xs text-ink">
                            <strong className="font-heading uppercase tracking-[0.15em] text-[10px]">Auto-Brief Dispatch</strong>
                            <span className="block text-ink-soft mt-0.5">
                                Receive a copy of every generated Brief in comms_outbound.
                            </span>
                        </span>
                    </label>
                </div>

                {err && (
                    <div className="paper-card border border-stamp-red p-3 mt-4">
                        <pre className="font-body text-xs text-stamp-red whitespace-pre-wrap">{err}</pre>
                    </div>
                )}

                <div className="mt-5 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border-2 border-ink/25 text-ink-soft font-heading uppercase text-[11px] tracking-[0.18em] hover:border-ink hover:text-ink"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy}
                        data-testid="edit-submit"
                        className="px-4 py-2 border-2 border-stamp-red bg-stamp-red text-paper font-heading uppercase text-[11px] tracking-[0.18em] hover:bg-ink disabled:opacity-50 flex items-center gap-2"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        Save amendment
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, multiline, testid }) {
    return (
        <div>
            <div className="label-typed mb-1">{label}</div>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={3}
                    data-testid={testid}
                    className="w-full px-3 py-2 border border-ink/30 bg-paper font-body text-sm text-ink focus:outline-none focus:border-stamp-red resize-none"
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                    className="w-full px-3 py-2 border border-ink/30 bg-paper font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                />
            )}
        </div>
    );
}
